import PageContent from "@/components/PageContent";
import Form from "@/components/Form";
import { QUESTION_TYPE_INPUT } from "@/schemas/question";
import { ApiError } from "@/services/api";
import { Question, QuestionType } from "@/types/question";
import { ReadSurvey, WriteSurvey } from "@/types/survey";
import { zodResolver } from "@hookform/resolvers/zod";
import { UseMutateFunction } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FieldErrors, useFieldArray, useForm } from "react-hook-form";
import type { VirtuosoHandle } from "react-virtuoso";
import SurveyEditorContext, {
  SurveyEditorContextType,
} from "./SurveyEditorContext";
import ArchiveAlert from "./components/ArchiveAlert";
import QuestionList from "./components/QuestionList";
import createQuestion from "./lib/createQuestion";
import setQuestionType from "./lib/setQuestionType";
import { readSurvey } from "@/schemas/survey";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";

interface SurveyFormProps {
  isNew?: boolean;
  values?: WriteSurvey;
  busy?: boolean;
  onSubmit: UseMutateFunction<ReadSurvey, ApiError, WriteSurvey>;
  buttons?: React.ReactNode;
  interQuestionToolbar?: (index: number) => React.ReactNode;
  hint?: string;
  icon?: React.ReactNode;
  title?: string;
}

const defaultValues: WriteSurvey = {
  name: "",
  description: "",
  questions: [createQuestion(QUESTION_TYPE_INPUT)],
};

export default function SurveyForm({
  isNew = false,
  values = defaultValues,
  busy = false,
  onSubmit,
  buttons,
  interQuestionToolbar,
  hint,
  icon,
  title,
}: SurveyFormProps) {
  const [disabled, setDisabled] = useState(false);
  const { can_create, can_update } = useModulePermissions();
  const hasPermission = isNew ? can_create : can_update;

  const form = useForm<WriteSurvey>({
    resolver: zodResolver(readSurvey),
    defaultValues: values,
    disabled: disabled || busy || !hasPermission,
  });

  const { fields, append, remove, move, insert, update } = useFieldArray({
    control: form.control,
    name: "questions",
  });

  const listRef = useRef<VirtuosoHandle>(null);

  const handleCleanSchema = useCallback(() => {
    form.reset(defaultValues);
  }, [form]);

  const setArchiveFlag = useCallback(
    (archived: boolean) => {
      onSubmit({
        ...form.getValues(),
        archived,
      });
    },
    [form, onSubmit],
  );

  const addQuestion = useCallback(
    ({
      question = createQuestion(QUESTION_TYPE_INPUT),
      insertAt,
    }: {
      question?: Question | Question[];
      insertAt?: number;
    }) => {
      const index = insertAt === undefined ? fields.length : insertAt + 1;
      if (insertAt === undefined) {
        append(question);
      } else {
        insert(index, question);
      }
      queueMicrotask(() => {
        listRef.current?.scrollToIndex({
          align: "center",
          index,
        });
      });
    },
    [append, insert, fields],
  );

  const switchQuestionType = useCallback(
    (index: number, type: QuestionType) => {
      const currentQuestion = fields[index];
      const newQuestion = setQuestionType(currentQuestion, type);
      update(index, newQuestion);
    },
    [update, fields],
  );

  const isQuestionRemoved = useCallback(
    (index: number) => !fields[index],
    [fields],
  );

  const context = useMemo<SurveyEditorContextType>(
    () => ({
      removeQuestion: (index: number) => remove(index),
      moveQuestion: (from: number, to: number) => move(from, to),
      addQuestion,
      isQuestionRemoved,
      switchQuestionType,
      handleCleanSchema,
      isNewSurvey: () => isNew,
      setArchiveFlag,
    }),
    [
      addQuestion,
      isQuestionRemoved,
      switchQuestionType,
      handleCleanSchema,
      setArchiveFlag,
      remove,
      move,
      isNew,
    ],
  );

  const handleSubmit = async (values: WriteSurvey) => {
    try {
      setDisabled(true);
      await onSubmit(values);
      setDisabled(false);
    } catch (error) {
      setDisabled(false);
      throw error;
    }
  };

  const scrollToFirstQuestionWithError = ({
    questions,
  }: FieldErrors<WriteSurvey>) => {
    for (const key in questions) {
      const index = parseInt(key);
      if (index !== undefined) {
        listRef.current?.scrollToIndex({
          align: "center",
          index,
        });
        break;
      }
    }
  };

  useEffect(() => {
    scrollToFirstQuestionWithError(form.formState.errors);
  }, [form.formState.errors]);

  return (
    <SurveyEditorContext.Provider value={context}>
      <Form form={form} handleSubmit={handleSubmit} className="h-full">
        <PageContent icon={icon} footer={buttons} title={title}>
          <ArchiveAlert show={values.archived} />
          <section className="px-2 h-full">
            <QuestionList
              fields={fields}
              listRef={listRef}
              interQuestionToolbar={interQuestionToolbar}
              hint={hint}
            />
          </section>
        </PageContent>
      </Form>
    </SurveyEditorContext.Provider>
  );
}
